<template>
  <div class="api-lookup-mapping-section h-100">
    <div class="mapping-section">
      <div class="mapping-header">
        <span>Vasion Fields</span>
        <span>External Values</span>
        <VasionTooltip :text="tooltipDisplayMessage" position="top">
          <span>Display</span>
        </VasionTooltip>
        <VasionTooltip :text="tooltipImportMessage" position="top">
          <span>Import</span>
        </VasionTooltip>
      </div>
      <div
        v-for="(mappingField, mappingFieldIndex) in mappingData"
        :key="mappingFieldIndex"
        class="mapping-record"
      >
        <p>{{ mappingField.fieldTitle }}</p>
        <VasionInput
          :id="`mappingfield-${mappingFieldIndex}`"
          v-model="mappingField.externalValue"
          title=""
          name="api-lookup-form-data-key"
          placeholder="Type a value or inject response data..."
          inputType="top-white"
          data-paste-target="true"
          @focus="handleInputFocus(mappingField, mappingFieldIndex)"
          @change="handleInputChange(mappingField, mappingFieldIndex)"
          @input="handleInputChange(mappingField, mappingFieldIndex)"
          @blur="checkForEmptyValue(mappingField, mappingFieldIndex)"
        />
        <VasionCheckbox
          id="display-checkbox"
          name="display-checkbox"
          :checked="mappingField.display"
          @change="
            mappingField.display = !mappingField.display
            emitSectionIsDirty()
          "
        />
        <VasionCheckbox
          id="import-checkbox"
          name="import-checkbox"
          :checked="mappingField.import"
          @change="
            mappingField.import = !mappingField.import
            emitSectionIsDirty()
          "
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ApiLookupMappingFormSection',
  components: {},
  props: {
    configurationData: {
      type: Object,
      required: true,
    },
    objectFields: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      editableData: this.configurationData,
      fields: this.objectFields,
      fieldsToIgnore: ['CreatedBy', 'Workflow_Initiator'],
      mappingData: [],
      previousInputValues: {},
      tooltipDisplayMessage:
        'Selecting this option allows you to see data but not import it. This helps when there are multiple records that are similar.',
      tooltipImportMessage:
        'Selecting import imports the data into Vasion to the Vasion mapped field.',
    }
  },
  watch: {
    configurationData: {
      handler(val) {
        if (val && val?.ApiResponse) {
          this.editableData = val
          this.mappingData = this.formatMappingData(
            this.editableData.ApiResponse,
            this.fields
          )
        }
      },
    },
    objectFields: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fields = val
        }
      },
    },
  },
  async created() {
    this.mappingData = this.createMappingArray(this.fields)

    if (this.editableData && this.editableData?.ApiResponse)
      this.mappingData = this.formatMappingData(
        this.editableData.ApiResponse,
        this.fields
      )
  },
  methods: {
    checkForEmptyValue(mappingField, mappingFieldIndex) {
      if (mappingField.externalValue === '') {
        this.mappingData[mappingFieldIndex].display = false
        this.mappingData[mappingFieldIndex].import = false
      }
    },
    createMappingArray(fields) {
      const mappingArray = []
      fields.forEach(field => {
        if (this.fieldsToIgnore.includes(field.name)) return

        mappingArray.push({
          fieldName: field.name,
          fieldTitle: field.title,
          externalValue: '',
          display: false,
          import: false,
        })
      })
      return mappingArray
    },
    emitSectionIsDirty() {
      const payload = {
        section: 'mapping',
        data: {
          mappingData: this.reverseMappingData(this.mappingData),
        },
      }
      this.$emit('markAsDirty', payload)
    },
    formatMappingData(inputObject, vasionFields) {
      const mappingArray = []
      vasionFields.forEach(field => {
        if (this.fieldsToIgnore.includes(field.name)) return

        const externalValue =
          field.name in inputObject
            ? inputObject[field.name]
            : `hidden_${field.name}` in inputObject
            ? inputObject[`hidden_${field.name}`]
            : ''
        const vasionFieldIsHidden = `hidden_${field.name}` in inputObject
        const vasionFieldIsPresent = field.name in inputObject

        mappingArray.push({
          fieldName: field.name,
          fieldTitle: field.title,
          externalValue: externalValue,
          display: vasionFieldIsPresent,
          import: vasionFieldIsHidden,
        })
      })
      return mappingArray
    },
    reverseMappingData(mappedArray) {
      return mappedArray.reduce((result, field) => {
        if (field.externalValue) {
          result[field.fieldName] = field.externalValue
        }
        if (field.display) {
          result[field.fieldName] = field.externalValue
        }
        if (field.import) {
          result[`hidden_${field.fieldName}`] = field.externalValue
        }
        return result
      }, {})
    },
    handleCheckboxChange(item, fieldName, newValue) {
      this.$set(item, fieldName, !newValue)
      this.emitSectionIsDirty()
    },
    setReactiveValue(mappingfield, value) {
      const indexStr = mappingfield.match(/-(\d+)$/)
      const index = parseInt(indexStr[1])
      this.mappingData[index].externalValue = value
      this.emitSectionIsDirty()

      this.handleInputChange(this.mappingData[index], index)
    },
    handleInputChange(mappingField, mappingFieldIndex) {
      const previousValue = this.previousInputValues[mappingFieldIndex] || ''
      const currentValue = mappingField.externalValue || ''

      if (previousValue.trim() === '' && currentValue.trim() !== '') {
        this.mappingData[mappingFieldIndex].import = true
      }

      this.previousInputValues[mappingFieldIndex] = currentValue

      this.emitSectionIsDirty()
    },
    handleInputFocus(mappingField, mappingFieldIndex) {
      this.previousInputValues[mappingFieldIndex] = mappingField.externalValue
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.api-lookup-mapping-section {
  padding: 16px;
}
.mapping-section {
  padding-bottom: 16px;
}
.mapping-header {
  background-color: $surface-variant;
  color: $on-surface-variant;
  padding: 16px;
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(320px, 4fr) 100px 100px;
  grid-gap: 8px;
}
.mapping-record {
  background-color: $surface-container;
  color: $on-surface-container;
  padding: 8px 16px;
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(320px, 4fr) 100px 100px;
  grid-gap: 8px;
}
.mapping-record > * {
  display: flex;
  align-items: center;
  justify-content: center;
}
.mapping-record > p {
  margin-bottom: 0;
  justify-content: flex-start;
}
.mapping-header > :nth-child(3),
.mapping-header > :nth-child(4),
.mapping-record > :nth-child(3),
.mapping-record > :nth-child(4) {
  margin: auto;
}
.mapping-record ::v-deep svg {
  margin: auto;
}
</style>
